package songwritersnotebook.app.utils

import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.di.PlatformDependencyProvider
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.delay

actual class AudioExporter actual constructor() {
    actual suspend fun exportSong(
        song: Song,
        chordLibrary: Map<String, ChordDefinition>,
        outputUri: String
    ) {
        val handler = PlatformDependencyProvider().getAudioHandler() as AudioHandler
        
        // 1. Prepare all assets (similar to StudioPlaybackEngine)
        val allItems = song.studioState?.tracks?.flatMap { it.items } ?: emptyList()
        val preparationDeferreds = mutableListOf<CompletableDeferred<Unit>>()
        
        val bpm = song.bpm.toFloat().coerceAtLeast(1f)
        val bps = bpm / 60f

        for (item in allItems) {
            if (item.type == "recording") {
                val deferred = CompletableDeferred<Unit>()
                preparationDeferreds.add(deferred)
                
                val trimStartMillis = ((item.trimStartBeats / bps) * 1000).toInt()
                val durationMillis = ((item.duration / bps) * 1000).toInt()
                
                val track = song.studioState?.tracks?.find { it.items.any { i -> i.id == item.id } }
                var trackVolume = track?.volume ?: 1.0f
                if (track?.name?.lowercase()?.contains("vocal") == true) trackVolume *= 4.0f

                handler.prepareTrackRecording(
                    itemId = item.id,
                    url = item.data.url ?: "",
                    volume = trackVolume,
                    startMillis = trimStartMillis,
                    durationMillis = durationMillis,
                    preparationDeferred = deferred
                )
            } else if (item.type == "note" || item.type == "chord") {
                // Ensure samples are loaded
                val instrument = item.data.instrument ?: "piano"
                val notes = if (item.type == "note") {
                    item.data.notes ?: emptyList()
                } else {
                    val chordDef = chordLibrary[item.data.name]
                    if (instrument.equals("guitar", ignoreCase = true)) {
                        chordDef?.guitarTab?.let { tab ->
                            val tabForConversion = tab.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }
                            ChordUtils.tabToNotes(tabForConversion, song.guitarTuning, song.guitarCapo)
                        } ?: emptyList()
                    } else chordDef?.pianoNotes ?: emptyList()
                }
                
                notes.forEach { note ->
                    handler.loadSample(instrument, note)
                }
            }
        }
        
        if (preparationDeferreds.isNotEmpty()) {
            preparationDeferreds.awaitAll()
        }
        
        // Small delay to ensure all decodes are finished (loadSample is async internally)
        delay(500)

        // 2. Render
        val buffer = handler.renderStudioProject(
            song.studioState?.tracks ?: emptyList(),
            song.bpm,
            chordLibrary,
            song
        )

        if (buffer != null) {
            // 3. Convert to WAV
            val uint8Array = SnBridge.audioBufferToWav(buffer)
            
            // Convert Uint8Array to ByteArray
            val bytes = ByteArray(uint8Array.length)
            for (i in 0 until uint8Array.length) {
                bytes[i] = uint8Array.asDynamic()[i] as Byte
            }
            
            // 4. Trigger Download
            val fileName = outputUri.removePrefix("web_download_").let { 
                if (it.endsWith(".m4a")) it.removeSuffix(".m4a") + ".wav" else it 
            }
            triggerWebDownload(fileName, bytes, "audio/wav")
        } else {
            throw Exception("Failed to render audio buffer.")
        }
    }
}
